import type { Difficulty, MCQQuestion, FillQuestion, GeneratedQuestion } from '../math/types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

/**
 * N-4-1：大數的認識（10000 以上，含萬、十萬）。
 * 四上第一章：萬以上整數量感、位值、大小比較、數線定位。
 *
 * difficulty 1：10000–99999（五位數，萬位比較）
 * difficulty 2：100000–999999（六位數，十萬位）
 * difficulty 3：10000–9999999（七位數以內，含估算）
 */
export function generateLargeNumber(seed: number, difficulty: Difficulty): GeneratedQuestion {
  const rand = mulberry32(seed);
  const qType = randInt(rand, 0, 2); // 0: compare largest, 1: place-value fill, 2: compare smallest

  if (qType === 1) {
    // Fill: 萬位數的位值讀法
    let n: number;
    if (difficulty === 1) {
      n = randInt(rand, 10000, 99999);
    } else if (difficulty === 2) {
      n = randInt(rand, 100000, 999999);
    } else {
      n = randInt(rand, 1000000, 9999999);
    }
    const wan = Math.floor(n / 10000);
    const correct = String(wan);
    return {
      kind: 'fill',
      prompt: `${n} 的萬位數字是多少？`,
      correctAnswer: correct,
      acceptedAnswers: [correct],
      explanation: `${n} 的萬位數字是 ${correct}（${wan} 個萬）。`,
      kp: 'math.large.number.4up',
    } satisfies FillQuestion;
  }

  // MCQ: 比大小
  const makeNums = (): number[] => {
    if (difficulty === 1) {
      return Array.from({ length: 4 }, () => randInt(rand, 10000, 99999));
    } else if (difficulty === 2) {
      return Array.from({ length: 4 }, () => randInt(rand, 100000, 999999));
    } else {
      return Array.from({ length: 4 }, () => randInt(rand, 10000, 9999999));
    }
  };

  const nums = makeNums();
  const unique = [...new Set(nums)];
  while (unique.length < 4) unique.push(unique[0]! + randInt(rand, 1, 1000));

  if (qType === 0) {
    // Which is largest?
    const largest = Math.max(...unique);
    const shuffled = [...unique].sort((a, b) => mulberry32(seed + a)() - mulberry32(seed + b)());
    return {
      kind: 'mcq',
      prompt: `下列哪個數最大？`,
      options: shuffled,
      correctAnswer: shuffled.indexOf(largest),
      explanation: `${shuffled.join('、')} 中，${largest} 最大。`,
      kp: 'math.large.number.4up',
    } satisfies MCQQuestion;
  } else {
    // Which is smallest?
    const smallest = Math.min(...unique);
    const shuffled = [...unique].sort((a, b) => mulberry32(seed + a + 3)() - mulberry32(seed + b + 3)());
    return {
      kind: 'mcq',
      prompt: `下列哪個數最小？`,
      options: shuffled,
      correctAnswer: shuffled.indexOf(smallest),
      explanation: `${shuffled.join('、')} 中，${smallest} 最小。`,
      kp: 'math.large.number.4up',
    } satisfies MCQQuestion;
  }
}
